import { apiRequest } from './client';
import type {
  HelpArticle,
  HelpArticleDetail,
  HelpArticleStatusResponse,
  HelpArticleUpsertPayload,
} from '../types/helpArticles';

export function getHelpArticles(accessToken: string, statusFilter?: string): Promise<readonly HelpArticle[]> {
  const query = statusFilter ? `?status_filter=${encodeURIComponent(statusFilter)}` : '';
  return apiRequest<readonly HelpArticle[]>(`/admin/help-articles${query}`, { token: accessToken });
}

export function getHelpArticle(articleId: string, accessToken: string): Promise<HelpArticleDetail> {
  return apiRequest<HelpArticleDetail>(`/admin/help-articles/${articleId}`, { token: accessToken });
}

export function createHelpArticle(payload: HelpArticleUpsertPayload, accessToken: string): Promise<HelpArticleDetail> {
  return apiRequest<HelpArticleDetail>('/admin/help-articles', {
    method: 'POST',
    body: payload,
    token: accessToken,
  });
}

export function updateHelpArticle(
  articleId: string,
  payload: HelpArticleUpsertPayload,
  accessToken: string,
): Promise<HelpArticleDetail> {
  return apiRequest<HelpArticleDetail>(`/admin/help-articles/${articleId}`, {
    method: 'PATCH',
    body: payload,
    token: accessToken,
  });
}

export function deleteHelpArticle(articleId: string, accessToken: string): Promise<void> {
  return apiRequest<void>(`/admin/help-articles/${articleId}`, {
    method: 'DELETE',
    token: accessToken,
  });
}

export function publishHelpArticle(articleId: string, accessToken: string): Promise<HelpArticleStatusResponse> {
  return apiRequest<HelpArticleStatusResponse>(`/admin/help-articles/${articleId}/publish`, {
    method: 'POST',
    token: accessToken,
  });
}

export function unpublishHelpArticle(articleId: string, accessToken: string): Promise<HelpArticleStatusResponse> {
  return apiRequest<HelpArticleStatusResponse>(`/admin/help-articles/${articleId}/unpublish`, {
    method: 'POST',
    token: accessToken,
  });
}

export function uploadHelpArticleHeroImage(
  articleId: string,
  file: File,
  accessToken: string,
): Promise<HelpArticleDetail> {
  const body = new FormData();
  body.append('file', file);
  return apiRequest<HelpArticleDetail>(`/admin/help-articles/${articleId}/hero-image`, {
    method: 'POST',
    body,
    token: accessToken,
  });
}

export function uploadHelpArticlePreviewImage(
  articleId: string,
  file: File,
  accessToken: string,
): Promise<HelpArticleDetail> {
  const body = new FormData();
  body.append('file', file);
  return apiRequest<HelpArticleDetail>(`/admin/help-articles/${articleId}/preview-image`, {
    method: 'POST',
    body,
    token: accessToken,
  });
}

export function deleteHelpArticlePreviewImage(
  articleId: string,
  accessToken: string,
): Promise<HelpArticleDetail> {
  return apiRequest<HelpArticleDetail>(`/admin/help-articles/${articleId}/preview-image`, {
    method: 'DELETE',
    token: accessToken,
  });
}

export function uploadHelpArticleHeroVideo(
  articleId: string,
  file: File,
  accessToken: string,
): Promise<HelpArticleDetail> {
  const body = new FormData();
  body.append('file', file);
  return apiRequest<HelpArticleDetail>(`/admin/help-articles/${articleId}/hero-video`, {
    method: 'POST',
    body,
    token: accessToken,
  });
}

export function uploadHelpArticleHeroPdf(
  articleId: string,
  file: File,
  accessToken: string,
): Promise<HelpArticleDetail> {
  const body = new FormData();
  body.append('file', file);
  return apiRequest<HelpArticleDetail>(`/admin/help-articles/${articleId}/hero-pdf`, {
    method: 'POST',
    body,
    token: accessToken,
  });
}

export function setHelpArticleYouTube(
  articleId: string,
  youtubeUrl: string,
  accessToken: string,
): Promise<HelpArticleDetail> {
  return apiRequest<HelpArticleDetail>(`/admin/help-articles/${articleId}/hero-youtube`, {
    method: 'POST',
    body: { youtube_url: youtubeUrl },
    token: accessToken,
  });
}

export function clearHelpArticleHeroMedia(
  articleId: string,
  accessToken: string,
): Promise<HelpArticleDetail> {
  return apiRequest<HelpArticleDetail>(`/admin/help-articles/${articleId}/hero-media`, {
    method: 'DELETE',
    token: accessToken,
  });
}
